// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useGooglePlaces } from '@/contexts/google-places-context';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { useMemo, useState } from 'react';

const DEFAULT_DELAY = 300;

export function useGooglePlacesPredictions(delay = DEFAULT_DELAY) {
  const { isReady, getPredictions } = useGooglePlaces();

  const [input, setInput] = useState('');

  const debouncedSearch = useMemo(() => debounce(setInput, delay), [delay]);

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['google-places', 'predictions', input],
    queryFn: () => {
      if (input === '') {
        return [];
      }

      return getPredictions(input);
    },
  });

  const reset = () => {
    setInput('');
  };

  const search = (value: string) => {
    if (value === '') {
      reset();
    } else {
      debouncedSearch(value);
    }
  };

  return {
    reset,
    search,
    isReady,
    predictions: query.data ?? [],
    isSearching: query.isFetching,
  };
}
